import React, {Component, ReactNode} from 'react'
import {Nullable} from '../../../app/TypeHelpers'
import {HOURS, RESERVATION_TIME_UNIT_MINUTES} from '../../../CodeMaster'
import TimePickerForResident from '../components/TimePickerForResident'
import {ReserveFacilityModel} from '../ReserveFacilityModel'
import {numberOrNull} from '../../../stringHelper/stringHelpers'
import DatePickerForResident from '../components/DatePickerForResident'
import {DisplayItemInputTitleRequiredNoMargin} from '../../../styledComponents/DisplayItem'
import {RequestItemError} from '../../../network/response/RequestItemError'
import {
    BlockSpacerSpOnly,
    GridBlockSpacerPcOnly,
    GridContainer,
    GridItem,
} from '../../../styledComponents/ResponsiveConvenience'
import styled from 'styled-components'
import PreformattedDiv from '../../../styledComponents/PreformattedDiv'
import {Colors, FontSize} from '../../../styledComponents/Constants'
import moment from 'moment'

interface Props {
    reservationModel: ReserveFacilityModel

    defaultStartDate: string
    defaultStartTime: Nullable<string>
    defaultEndDate: Nullable<string>
    defaultEndTime: Nullable<string>

    onChange: (startDate: string,
               startHour: Nullable<HOURS>,
               startMinute: Nullable<RESERVATION_TIME_UNIT_MINUTES>,
               endDate: Nullable<string>,
               endHour: Nullable<HOURS>,
               endMinute: Nullable<RESERVATION_TIME_UNIT_MINUTES>,
    ) => void

    requestItemErrors: Nullable<RequestItemError[]>
    hasError?: boolean
    currentDateTime: moment.Moment
    holidays: string[]
}

interface State {
    startDate: string
    startHour: Nullable<HOURS>
    startMinute: Nullable<RESERVATION_TIME_UNIT_MINUTES>
    endDate: Nullable<string>
    endHour: Nullable<HOURS>
    endMinute: Nullable<RESERVATION_TIME_UNIT_MINUTES>
}

class RRFFReserveDataInputDurationFacility extends Component<Props, State> {
    constructor(props: Readonly<Props>) {
        super(props)

        const start = splitTime(props.defaultStartTime) || {hour: null, minute: null}
        const end = splitTime(props.defaultEndTime) || {hour: null, minute: null}
        this.state = {
            startDate: this.props.defaultStartDate,
            startHour: start.hour,
            startMinute: start.minute,
            endDate: this.props.defaultEndDate,
            endHour: end.hour,
            endMinute: end.minute,
        }

        this.handleChangeStartDate = this.handleChangeStartDate.bind(this)
        this.handleChangeStartTime = this.handleChangeStartTime.bind(this)
        this.handleChangeEndDate = this.handleChangeEndDate.bind(this)
        this.handleChangeEndTime = this.handleChangeEndTime.bind(this)
    }

    componentDidMount(): void {
        this.updateState()
    }

    updateState(): void {
        const start = splitTime(this.props.defaultStartTime) || {hour: null, minute: null}
        const end = splitTime(this.props.defaultEndTime) || {hour: null, minute: null}
        this.setState({
            startDate: this.props.defaultStartDate,
            startHour: start.hour,
            startMinute: start.minute,
            endDate: this.props.defaultEndDate,
            endHour: end.hour,
            endMinute: end.minute,
        }, () => {
            this.handleChange()
        })
    }

    handleChange(): void {
        this.props.reservationModel.withDurationReservationData(
            this.state.startDate,
            formatTimeString(this.state.startHour, this.state.startMinute),
            this.state.endDate,
            formatTimeString(this.state.endHour, this.state.endMinute),
        )
        this.props.onChange(
            this.state.startDate,
            this.state.startHour,
            this.state.startMinute,
            this.state.endDate,
            this.state.endHour,
            this.state.endMinute,
        )
    }

    handleChangeStartDate(startDate: string): void {
        this.setState({startDate}, () => {
            this.handleChange()
        })
    }

    handleChangeStartTime(startHour: Nullable<HOURS>, startMinute: Nullable<RESERVATION_TIME_UNIT_MINUTES>): void {
        this.setState({
            startHour,
            startMinute,
        }, () => {
            this.handleChange()
        })
    }

    handleChangeEndDate(endDate: string): void {
        this.setState({endDate}, () => {
            this.handleChange()
        })
    }

    handleChangeEndTime(endHour: Nullable<HOURS>, endMinute: Nullable<RESERVATION_TIME_UNIT_MINUTES>): void {
        this.setState({
            endHour,
            endMinute,
        }, () => {
            this.handleChange()
        })
    }

    render(): ReactNode {
        const {startDate, startHour, startMinute, endDate, endHour, endMinute} = this.state
        const {hasError, currentDateTime, holidays} = this.props

        return (<GridContainer>
            <GridItem over={3}>
                <DisplayItemInputTitleRequiredNoMargin>
                    開始日時
                </DisplayItemInputTitleRequiredNoMargin>
            </GridItem>
            <GridItem over={5}>
                <BlockSpacerSpOnly height={'10px'}/>
                <DatePickerForResident
                    date={startDate}
                    selectableStartDate={currentDateTime}
                    onChange={this.handleChangeStartDate}
                    hasError={hasError}
                    holidays={holidays}
                />
            </GridItem>
            <GridItem over={4}>
                <BlockSpacerSpOnly height={'10px'}/>
                <TimePickerForResident
                    hour={startHour} minute={startMinute}
                    onChange={this.handleChangeStartTime}
                    hassError={hasError}/>
            </GridItem>

            <GridBlockSpacerPcOnly height={'10px'}/>

            <GridItem over={3}>
                <BlockSpacerSpOnly height={'10px'}/>
                <DisplayItemInputTitleRequiredNoMargin>
                    終了日時
                </DisplayItemInputTitleRequiredNoMargin>
            </GridItem>
            <GridItem over={5}>
                <BlockSpacerSpOnly height={'10px'}/>
                <DatePickerForResident
                    date={endDate}
                    selectableStartDate={currentDateTime}
                    onChange={this.handleChangeEndDate}
                    hasError={hasError}
                    holidays={holidays}
                />
            </GridItem>
            <GridItem over={4}>
                <BlockSpacerSpOnly height={'10px'}/>
                <TimePickerForResident
                    hour={endHour} minute={endMinute}
                    onChange={this.handleChangeEndTime}
                    hassError={hasError}/>
            </GridItem>
            {
                (this.props.requestItemErrors !== null) &&
                <ErrorItem>
                    {
                        this.props.requestItemErrors.map((item, i) => <div key={i}>{item.message}</div>)
                    }
                </ErrorItem>
            }
        </GridContainer>)
    }
}

const formatTimeString = (hour: Nullable<HOURS>, minute: Nullable<RESERVATION_TIME_UNIT_MINUTES>): Nullable<string> => {
    if (hour === null || minute === null) {
        return null
    }
    const hourString: string = `0${hour.label}`.substr(-2)
    const minuteString: string = `0${minute.label}`.substr(-2)
    return `${hourString}:${minuteString}`
}


const splitTime = (timeString: Nullable<string>): Nullable<{ hour: HOURS, minute: RESERVATION_TIME_UNIT_MINUTES }> => {
    if (timeString === null) {
        return null
    }
    const splitResult: Nullable<string[]> = timeString.match(/^(\d{1,2}):(\d{1,2})$/)
    if (splitResult === null) {
        return null
    }

    let hour: Nullable<HOURS>
    let minute: Nullable<RESERVATION_TIME_UNIT_MINUTES>
    try {
        hour = HOURS.codeToEnumConstants(`${numberOrNull(splitResult[1])}`)
        minute = RESERVATION_TIME_UNIT_MINUTES.codeToEnumConstants(`${numberOrNull(splitResult[2])}`)
    } catch (error) {
        return null
    }
    return {
        hour: hour,
        minute: minute,
    }
}

export default RRFFReserveDataInputDurationFacility

const ErrorItem = styled(PreformattedDiv)`
  color: ${Colors.AccentDark};
  font-size: ${FontSize.Small};
`
